import React from "react";
import { FINANCING_URL } from "@/data/financing";
import { trackEvent, locationOf } from "@/lib/analytics";

/**
 * The only way the site links to Acorn Finance. Enforces every financing-link
 * rule in one place: the exact URL, a new tab with rel="noopener noreferrer",
 * an aria-label that ends in "(opens in a new tab)", a data-placement marker
 * and a `financing_click` GTM event carrying `placement` (unique per CTA) and
 * `location` (header / top_bar / sticky_bar / hero / page / footer). Works as a plain anchor or as the child of
 * <Button asChild> (Slot merges className / handlers onto this anchor).
 *
 * @param {string} placement  e.g. "financing-hero" — required, unique per CTA.
 * @param {string} [label]    accessible name when `children` is not plain text
 *                            (icons etc.); defaults to the text children.
 */
export const FinancingLink = React.forwardRef(function FinancingLink(
  { placement, label, children, className, onClick, ...rest },
  ref,
) {
  const name = label || (typeof children === "string" ? children : "Check your rate");
  const handleClick = (event) => {
    trackEvent("financing_click", { placement, location: locationOf(event.currentTarget) });
    if (onClick) onClick(event);
  };
  return (
    <a
      ref={ref}
      href={FINANCING_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${name} (opens in a new tab)`}
      data-placement={placement}
      data-testid={`financing-link-${placement}`}
      className={className}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </a>
  );
});
